import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";

const HERO_IMG = "/assets/stjames-house.jpg";

export default function Login({ onLogin }) {
  const [family, setFamily] = useState([]);
  const [openName, setOpenName] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getFamily().then((data) => {
      setFamily(data);
    });
  }, []);

  const handleSelect = (name, existingEmail, existingPhone) => {
    setOpenName((prev) => (prev === name ? null : name));
    setEmail(existingEmail || "");
    setPhone(existingPhone || "+91 ");
  };

  const handleSubmit = async (e, name) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    const cleanPhone = phone.replace(/[^\d+]/g, "");
    if (cleanPhone && cleanPhone !== "+91" && !/^\+?\d{8,15}$/.test(cleanPhone)) {
      toast.error("Please enter a valid WhatsApp number with country code");
      return;
    }
    setSubmitting(true);
    try {
      const member = await api.login(
        name,
        email,
        cleanPhone && cleanPhone !== "+91" ? cleanPhone : null
      );
      toast.success(`Welcome home, ${name}.`);
      onLogin(member);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-12"
      data-testid="login-screen"
    >
      {/* Left atmospheric image */}
      <div className="relative lg:col-span-6 min-h-[42vh] lg:min-h-screen overflow-hidden bg-[#1A2F24]">
        <img
          src={HERO_IMG}
          alt="StJames in the Mahabaleshwar hills"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/70" />
        <div className="relative h-full flex flex-col justify-between p-10 lg:p-14 text-[#FAF9F6]">
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-[#FAF9F6]/70" />
            <p className="tracker text-[11px] text-[#FAF9F6]/90">
              Est. Mahabaleshwar
            </p>
          </div>
          <div>
            <img
              src="/assets/stjames-wordmark-light.png"
              alt="StJames"
              data-testid="hero-logo"
              className="h-24 sm:h-28 lg:h-32 w-auto select-none"
              draggable="false"
            />
            <p className="font-body text-sm text-[#FAF9F6]/70 mt-6 max-w-sm">
              A private guestbook & calendar for our house in the misty hills.
              Tell us who you are and the doors swing open.
            </p>
          </div>
        </div>
      </div>

      {/* Right roster */}
      <div className="lg:col-span-6 flex items-center bg-[#FAF9F6]">
        <div className="w-full max-w-xl mx-auto px-8 sm:px-12 py-14">
          <p className="tracker text-[11px] text-[#9E473F]" data-testid="login-section-label">
            The Family Roster
          </p>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 text-[#1A2F24] leading-tight">
            Who&apos;s arriving?
          </h2>
          <p className="font-body text-sm text-[#5C6B61] mt-3 max-w-md">
            Tap your name. Drop your email. The calendar will know you.
          </p>

          <ul className="mt-10 divide-y divide-[#DCE0D9] border-t border-b border-[#DCE0D9]">
            {family.map((member, i) => {
              const open = openName === member.name;
              return (
                <motion.li
                  key={member.name}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.04 * i, duration: 0.4 }}
                  className="group"
                >
                  <button
                    type="button"
                    onClick={() => handleSelect(member.name, member.email, member.phone)}
                    data-testid={`family-member-${member.name.toLowerCase()}`}
                    className="w-full flex items-center justify-between py-5 px-1 text-left transition-colors duration-300 hover:bg-[#F3F4F1]"
                  >
                    <span className="flex items-center gap-4">
                      <span
                        className="inline-block h-2 w-8"
                        style={{ background: member.color }}
                      />
                      <span className="font-display text-2xl sm:text-3xl text-[#1A2F24]">
                        {member.name}
                      </span>
                      {member.email && (
                        <span className="tracker text-[9px] text-[#5C6B61]">
                          known
                        </span>
                      )}
                    </span>
                    <span
                      className={`font-display text-3xl transition-transform duration-300 ${
                        open ? "rotate-45 text-[#9E473F]" : "text-[#1A2F24]"
                      }`}
                    >
                      +
                    </span>
                  </button>

                  {open && (
                    <motion.form
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.25 }}
                      onSubmit={(e) => handleSubmit(e, member.name)}
                      className="pb-6 pt-2 px-1 flex flex-col gap-3"
                    >
                      <input
                        type="email"
                        autoFocus
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={`${member.name.toLowerCase()}@example.com`}
                        data-testid={`email-input-${member.name.toLowerCase()}`}
                        className="w-full bg-transparent border-b border-[#1A2F24] py-2 px-0 font-body text-base text-[#1A2F24] placeholder:text-[#5C6B61]/60 outline-none focus:border-[#9E473F]"
                      />
                      <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                          <input
                            type="tel"
                            inputMode="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+91 98xxx xxxxx (WhatsApp, optional)"
                            data-testid={`phone-input-${member.name.toLowerCase()}`}
                            className="w-full bg-transparent border-b border-[#DCE0D9] py-2 px-0 font-body text-base text-[#1A2F24] placeholder:text-[#5C6B61]/60 outline-none focus:border-[#9E473F]"
                          />
                          <p className="font-body text-[10px] text-[#5C6B61] mt-1">
                            WhatsApp number · optional, for booking alerts
                          </p>
                        </div>
                        <button
                          type="submit"
                          disabled={submitting}
                          data-testid={`enter-btn-${member.name.toLowerCase()}`}
                          className="bg-[#1A2F24] text-[#FAF9F6] px-6 py-3 font-body text-sm tracker hover:bg-[#2C4A3A] transition-colors duration-300 disabled:opacity-60 self-start"
                        >
                          {submitting ? "Opening..." : "Enter"}
                        </button>
                      </div>
                    </motion.form>
                  )}
                </motion.li>
              );
            })}
          </ul>

          <p className="font-body text-xs text-[#5C6B61] mt-8">
            By entering, you&apos;ll get email (and WhatsApp, if you add a number) alerts for family bookings.
          </p>
        </div>
      </div>
    </div>
  );
}
